import './style.css';
import CursorTrail from './CursorTrail';
import { profile } from './data/profile';
import { useEffect, useRef, useState } from 'react';

const PROMPT = `${profile.promptUser}@${profile.promptHost}:~$`;
const HELP_COMMANDS = [
  { name: 'about' },
  { name: 'email' },
  { name: 'clear' }
];
const INTRO_ENTRIES = [
  { type: 'output', text: 'Welcome. Type `help` to see available commands.' }
];

function normalizeCommand(command) {
  return command.trim().toLowerCase().replace(/\s+/g, ' ');
}

function codeLine(segments) {
  return {
    type: 'code-line',
    segments
  };
}

function buildAboutOutput() {
  const focusLines = profile.focus.flatMap((item, index) => {
    const isLastItem = index === profile.focus.length - 1;

    return [
      codeLine([
        { text: '    ' },
        { text: `'${item}'`, className: 'code-string' },
        { text: isLastItem ? '' : ',', className: 'code-punctuation' }
      ])
    ];
  });
  const socialLinkLines = profile.socialLinks.flatMap((item, index) => {
    const isLastItem = index === profile.socialLinks.length - 1;

    return [
      codeLine([
        { text: '    ' },
        { text: `'${item.label}'`, className: 'code-string code-link', href: item.href },
        { text: isLastItem ? '' : ',', className: 'code-punctuation' }
      ])
    ];
  });

  return [
    codeLine([
      { text: 'const', className: 'code-keyword' },
      { text: ' me ' },
      { text: '=', className: 'code-operator' },
      { text: ' ', },
      { text: '{', className: 'code-punctuation' }
    ]),
    codeLine([
      { text: '  name', className: 'code-key' },
      { text: ': ', className: 'code-punctuation' },
      { text: `'${profile.name}'`, className: 'code-string' },
      { text: ',', className: 'code-punctuation' },
    ]),
    codeLine([
      { text: '  profession', className: 'code-key' },
      { text: ': ', className: 'code-punctuation' },
      { text: `'${profile.role}'`, className: 'code-string' },
      { text: ',', className: 'code-punctuation' }
    ]),
    // codeLine([
    //   { text: '  identity', className: 'code-key' },
    //   { text: ': ', className: 'code-punctuation' },
    //   { text: `'${profile.identity}'`, className: 'code-string' },
    //   { text: ',', className: 'code-punctuation' }
    // ]),
    codeLine([
      { text: '  about', className: 'code-key' },
      { text: ': ', className: 'code-punctuation' },
      { text: `'${profile.about}'`, className: 'code-string' },
      { text: ',', className: 'code-punctuation' }
    ]),
    codeLine([
      { text: '  current_focus', className: 'code-key' },
      { text: ': ', className: 'code-punctuation' },
      { text: '[', className: 'code-punctuation' }
    ]),
    ...focusLines,
    codeLine([
      { text: '  ', },
      { text: ']', className: 'code-punctuation' },
      { text: ',', className: 'code-punctuation' }
    ]),
    codeLine([
      { text: '  social_media_links', className: 'code-key' },
      { text: ': ', className: 'code-punctuation' },
      { text: '[', className: 'code-punctuation' }
    ]),
    ...socialLinkLines,
    codeLine([
      { text: '  ', },
      { text: ']', className: 'code-punctuation' }
    ]),
    codeLine([
      { text: '}', className: 'code-punctuation' },
      { text: ';', className: 'code-punctuation' }
    ])
  ];
}

const commandDefinitions = {
  help: {
    description: 'Show available commands',
    run: () => [
      { type: 'output', text: 'Available commands:' },
      { type: 'blank' },
      ...HELP_COMMANDS.map(({ name, label }) => ({
        type: 'help-command',
        name: label || name,
        description: commandDefinitions[name].description,
      }))
    ]
  },
  about: {
    description: 'Show profile info',
    run: () => buildAboutOutput()
  },
  email: {
    description: 'Show my email',
    run: () => {
      if (!profile.email) {
        return [
          { type: 'muted', text: 'Email has not been configured yet.' }
        ];
      }

      return [
        { type: 'output', text: profile.email }
      ];
    },
  },
  clear: {
    description: 'Clear the terminal',
    run: () => 'CLEAR'
  }
};

function getCommandOutput(command) {
  return commandDefinitions[command]?.run();
}

function isInteractiveTarget(target) {
  return target.closest?.('a, button, input, textarea, select, [contenteditable="true"]');
}

function TerminalHeader() {
  return (
    <div className="terminal-header">
      <div className="terminal-controls" aria-hidden="true">
        <span className="terminal-dot terminal-dot-red" />
        <span className="terminal-dot terminal-dot-yellow" />
        <span className="terminal-dot terminal-dot-green" />
      </div>
      <div className="terminal-title">{profile.domain}</div>
    </div>
  );
}

function OutputEntry({ entry }) {
  if (entry.type === 'command') {
    return (
      <div className="terminal-line terminal-command">
        <span className="terminal-prompt">{PROMPT}</span>
        <span className="terminal-command-text">{entry.command}</span>
      </div>
    );
  }

  if (entry.type === 'blank') {
    return <div className="terminal-line" aria-hidden="true">&nbsp;</div>;
  }

  if (entry.type === 'help-command') {
    return (
      <div className="terminal-line help-command">
        <span className="help-command-name">{entry.name}</span>
        <span>{entry.description}</span>
      </div>
    );
  }

  if (entry.type === 'link' || entry.type === 'email') {
    const opensNewTab = !entry.href.startsWith('mailto:');

    return (
      <div className="terminal-line">
        <a
          className="terminal-link"
          href={entry.href}
          target={opensNewTab ? '_blank' : undefined}
          rel={opensNewTab ? 'noreferrer' : undefined}
        >
          {entry.text}
        </a>
      </div>
    );
  }

  if (entry.type === 'code-line') {
    return (
      <div className="terminal-line code-output">
        {entry.segments.map((segment, index) => {
          if (segment.href) {
            return (
              <a
                className={segment.className || undefined}
                href={segment.href}
                target="_blank"
                rel="noreferrer"
                key={`${segment.text}-${index}`}
              >
                {segment.text}
              </a>
            );
          }

          return (
            <span className={segment.className || undefined} key={`${segment.text}-${index}`}>
              {segment.text}
            </span>
          );
        })}
      </div>
    );
  }

  if (entry.type === 'heading' || entry.type === 'subheading' || entry.type === 'boot') {
    return (
      <div className={`terminal-line terminal-${entry.type}`}>
        {entry.text}
      </div>
    );
  }

  if (entry.type === 'bullet') {
    return (
      <div className="terminal-line terminal-bullet">
        <span aria-hidden="true">- </span>
        {entry.text}
      </div>
    );
  }

  return (
    <div className={`terminal-line ${entry.type === 'error' ? 'terminal-error' : ''} ${entry.type === 'muted' ? 'terminal-muted' : ''}`}>
      {entry.text}
    </div>
  );
}

function App() {
  const [entries, setEntries] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(null);
  const inputRef = useRef(null);
  const outputEndRef = useRef(null);

  const focusInput = ({ immediate = false } = {}) => {
    const focus = () => {
      const input = inputRef.current;

      if (!input) {
        return;
      }

      try {
        input.focus({ preventScroll: true });
      } catch {
        input.focus();
      }
    };

    if (immediate) {
      focus();
      return;
    }

    window.requestAnimationFrame(focus);
  };

  const handleTerminalClick = (event) => {
    if (isInteractiveTarget(event.target)) {
      return;
    }

    focusInput({ immediate: true });
  };

  const runCommand = (rawValue) => {
    const rawCommand = rawValue.trim();

    if (!rawCommand) {
      setInputValue('');
      focusInput();
      return;
    }

    const command = normalizeCommand(rawCommand);
    const output = getCommandOutput(command);

    setInputValue('');

    if (output === 'CLEAR') {
      setEntries([]);
      setHistory([]);
      setHistoryIndex(null);
      focusInput();
      return;
    }

    setHistory((currentHistory) => [...currentHistory, rawCommand]);
    setHistoryIndex(null);

    const commandEntry = { type: 'command', command: rawCommand };
    const outputEntries = output
      ? output
      : [
        { type: 'error', text: `Command not found: ${rawCommand}` },
        { type: 'muted', text: 'Type `help` to see available commands.' },
      ];

    setEntries((currentEntries) => [
      ...currentEntries,
      commandEntry,
      ...outputEntries,
    ]);
    focusInput();
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    runCommand(inputValue);
  };

  const handleKeyDown = (event) => {
    if (event.ctrlKey && event.key.toLowerCase() === 'l') {
      event.preventDefault();
      setEntries([]);
      setInputValue('');
      setHistory([]);
      setHistoryIndex(null);
      focusInput();
      return;
    }

    if (event.key === 'Tab') {
      event.preventDefault();
      const commandPrefix = normalizeCommand(inputValue);

      if (!commandPrefix) {
        return;
      }

      const match = Object.keys(commandDefinitions).find((name) => name.startsWith(commandPrefix));

      if (match) {
        setInputValue(match);
      }

      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();

      if (!history.length) {
        return;
      }

      const nextIndex = historyIndex === null
        ? history.length - 1
        : Math.max(0, historyIndex - 1);

      setHistoryIndex(nextIndex);
      setInputValue(history[nextIndex]);
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();

      if (!history.length || historyIndex === null) {
        return;
      }

      if (historyIndex === history.length - 1) {
        setHistoryIndex(null);
        setInputValue('');
        return;
      }

      const nextIndex = historyIndex + 1;
      setHistoryIndex(nextIndex);
      setInputValue(history[nextIndex]);
    }
  };

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    outputEndRef.current?.scrollIntoView({ block: 'end' });
  }, [entries]);

  return (
    <main className="terminal-page" onClick={handleTerminalClick}>
      <CursorTrail />
      <section className="terminal-shell" aria-label={`${profile.name} terminal homepage`}>
        <TerminalHeader />

        <div className="terminal-body">
          <div className="terminal-output" aria-live="polite">
            {INTRO_ENTRIES.map((entry, index) => (
              <OutputEntry
                key={`intro-${entry.type}-${index}`}
                entry={entry}
              />
            ))}

            {
              entries.map((entry, index) => (
                <OutputEntry
                  key={`${entry.type}-${entry.text || entry.command || entry.name || index}-${index}`}
                  entry={entry}
                />
              ))
            }

            <div ref={outputEndRef} />
          </div>

          <form className="terminal-input-line" onSubmit={handleSubmit}>
            <label className="sr-only" htmlFor="terminal-command">
              Type a terminal command
            </label>
            <span className="terminal-prompt" aria-hidden="true">{PROMPT}</span>
            <span className="terminal-input-wrap">
              <input
                id="terminal-command"
                ref={inputRef}
                value={inputValue}
                style={{ width: `${Math.max(inputValue.length + 0.75, 1.75)}ch` }}
                onChange={(event) => setInputValue(event.target.value)}
                onKeyDown={handleKeyDown}
                autoComplete="off"
                autoCapitalize="none"
                autoCorrect="off"
                enterKeyHint="send"
                spellCheck="false"
                aria-label="Terminal command"
              />
              <span className="terminal-cursor" aria-hidden="true" />
            </span>
          </form>
        </div>
      </section>
    </main>
  );
}

export default App;
